import { cn } from '@ophelos/ui/lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

interface CountdownProps {
  to: string | Date | null | undefined;
  className?: string;
  /** Text shown once the deadline has passed. Defaults to a translated "Offer expired". Pass null to render nothing. */
  expiredLabel?: string | null;
}

function diffParts(ms: number): { days: number; hours: number; minutes: number } {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  return { days, hours, minutes };
}

export function Countdown({ to, className, expiredLabel }: CountdownProps) {
  const { t } = useTranslation();
  const deadline = to ? new Date(to).getTime() : null;
  const [remaining, setRemaining] = useState<number>(deadline ? deadline - Date.now() : 0);

  useEffect(() => {
    if (!deadline) return;
    setRemaining(deadline - Date.now());
    const id = setInterval(() => setRemaining(deadline - Date.now()), 60_000);
    return () => clearInterval(id);
  }, [deadline]);

  if (!deadline) return null;
  if (remaining <= 0) {
    const label = expiredLabel === undefined ? t('countdown.offerExpired') : expiredLabel;
    if (!label) return null;
    return <span className={cn('text-xs text-muted-foreground', className)}>{label}</span>;
  }

  const { days, hours, minutes } = diffParts(remaining);
  const formatted =
    days > 0
      ? t('countdown.expiresInDaysHours', { days, hours })
      : hours > 0
        ? t('countdown.expiresInHoursMinutes', { hours, minutes: String(minutes).padStart(2, '0') })
        : t('countdown.expiresInMinutes', { minutes: String(minutes).padStart(2, '0') });

  return <span className={cn('text-xs font-medium text-foreground tabular-nums', className)}>{formatted}</span>;
}
